import { config, wagmiAdapter, projectId, networks } from "@/configs"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { lazy, Suspense, type ReactNode } from "react"
import { createAppKit } from "@reown/appkit/react"
import { WagmiProvider } from "wagmi"
import { bscTestnet, mainnet } from "@reown/appkit/networks"
import { AppLoading } from "@/components/AppLoading"

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((module) => ({
        default: module.ReactQueryDevtools,
      }))
    )
  : null

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
  },
})

if (!projectId) {
  throw new Error("Project ID is not defined")
}

const metadata = {
  name: "Avacus Gas Sponsor",
  description: "Avacus Gas Sponsor",
  url: import.meta.env.VITE_WEB_URL ?? window.location.origin,
  icons: ["https://avacus.cc/static/apple-touch-icon.png"],
}

const defaultNetwork =
  import.meta.env.VITE_BLOCKCHAIN === "mainnet" ? mainnet : bscTestnet

createAppKit({
  metadata,
  adapters: [wagmiAdapter],
  projectId,
  networks: [defaultNetwork, ...networks],
  defaultNetwork,
  themeMode: "light",
  features: {
    analytics: false,
    email: false,
    socials: false,
    swaps: false,
    history: false,
    onramp: false,
  },
  allWallets: "ONLY_MOBILE",
  allowUnsupportedChain: true,
  includeWalletIds: [
    "94f785c0c8fb8c4f38cd9cd704416430bcaa2137f27e1468782d624bcd155a43",
  ],
  themeVariables: {
    "--w3m-accent": "var(--color-primary)",
    "--w3m-color-mix": "var(--color-accent)",
    "--w3m-z-index": 999999999,
  },
})

function Web3WalletProvider({ children }: { children: ReactNode }) {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <AppLoading>
          {children}
          {ReactQueryDevtools ? (
            <Suspense fallback={null}>
              <ReactQueryDevtools />
            </Suspense>
          ) : null}
        </AppLoading>
      </QueryClientProvider>
    </WagmiProvider>
  )
}

export default Web3WalletProvider
